import camelcaseKeys from 'camelcase-keys';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import getProductLineLetter from '../../utils/api/getProductLineLetter';
import transformSaveBuildRequest from '../apiResponseTransformers/transformSaveBuildRequest';
import useBapGetBuildData from './useBapGetBuildData';
import useBapGetComparedData from './saved_build/useBapGetComparedData';
import useBapLoadBuild from '../../components/ProgressBar/hooks/useBapLoadBuild';
import useFetchCurrentVehicleFinancialCalculatorSummary from './useFetchSummary';
import useFetchLegacyProtection from './useFetchLegacyProtection';
import useWarranties from '../../components/WarrantyOptions/useWarranties';
import useIncentiveOffers, { filterOutNonIncentiveOffers, isLoyaltyOffer, } from '../../components/PaymentCalculator/ViewOffers/useIncentiveOffers';
import useUserAuth, { useFetchUserAuth } from './useUserAuth';
import { ACCOUNT_LOGIN_URL } from '../../utils/constants';
import { getProtectionPrice } from '../../components/ConfigureBap/utils/utils';
import { persistCurrentVehicleToUrl, selectCurrentVehicleAccessoryKeys, selectCurrentVehicleProtectionKeys, updateCurrentVehicleField, } from '../reducers/currentVehicle';
import { persistPrimaryCalculatorToUrl, primaryCalculatorId, selectPrimaryCalculatorPaymentMethod, selectTermAndRate, updateCalculatorField, } from '../reducers/financialCalculators';
import { selectCurrentVehicleFromVehicles, selectFlattenedCurrentVehicleFromVehicles, selectTransmissionFromCurrentVehicle, } from '../reducers/vehicles';
import { useAccessoriesFetch } from './useAccessoriesFetch';
import { useApiHost, useAppName, useLanguage, useSharedApps } from '../../utils/sitecoreContext';
import { useAppDispatch, useAppSelector } from '../utils';
import { useFetchCurrentVehicleFinancialCalculatorPayment } from './useFetchPayment';
const useSaveBuild = (buildName, isOnlineCreditApplication) => {
    var _a, _b, _c, _d, _e;
    const [isLoading, setIsLoading] = useState(false);
    const { search } = useLocation();
    const query = new URLSearchParams(search);
    const queryBuildId = query.get('buildId');
    const appName = useAppName();
    const hondaRestApiHost = useApiHost();
    const language = useLanguage();
    const productLineLetter = getProductLineLetter.get(appName);
    const dispatch = useAppDispatch();
    const currentVehicle = useAppSelector(selectFlattenedCurrentVehicleFromVehicles);
    const currentTransmission = useAppSelector(selectTransmissionFromCurrentVehicle);
    const paymentMethod = useAppSelector(selectPrimaryCalculatorPaymentMethod);
    const accountLoginUrl = useSharedApps(ACCOUNT_LOGIN_URL);
    const currentVehicleAccessories = useAppSelector(selectCurrentVehicleAccessoryKeys);
    const termAndRate = useAppSelector(state => selectTermAndRate(state, primaryCalculatorId));
    const savedBuildData = useBapGetBuildData();
    const accessoriesData = useAccessoriesFetch((currentTransmission === null || currentTransmission === void 0 ? void 0 : currentTransmission.detIdentifier) || '');
    const paymentData = useFetchCurrentVehicleFinancialCalculatorPayment(primaryCalculatorId);
    const currentVehicleAccessoriesSet = new Set(currentVehicleAccessories);
    const accessoriesPayment = Object.fromEntries(((_a = paymentData === null || paymentData === void 0 ? void 0 : paymentData.data) === null || _a === void 0 ? void 0 : _a.accessories.map(({ frequencyPayment, key }) => [key, frequencyPayment])) || []);
    const selectedAccessories = (_b = accessoriesData === null || accessoriesData === void 0 ? void 0 : accessoriesData.data) === null || _b === void 0 ? void 0 : _b.filter(a => currentVehicleAccessoriesSet.has(a.businessKey) || a.isIncluded).map(accessory => ({
        Key: accessory.businessKey,
        Name: accessory.name,
        Value: accessory.provincialPrice,
        FrequencyPayment: accessoriesPayment[accessory.businessKey] || 0,
    }));
    const { selectedIncentiveOffers, selectedLoyaltyOffers, allAvailableOffers, loyaltyOffers } = useIncentiveOffers(primaryCalculatorId, false, false);
    const selectedOffers = useMemo(() => {
        const selectedOfferIds = new Set(selectedIncentiveOffers);
        return allAvailableOffers
            .filter(({ id, key }) => selectedOfferIds.has(id) || (!!selectedLoyaltyOffers && key === selectedLoyaltyOffers))
            .map(({ id, key, name, type, value, term }) => ({
            Id: isLoyaltyOffer(type) ? key : id,
            Name: name,
            Type: type,
            Value: value,
            ExpiryDate: term || null,
        }));
    }, [allAvailableOffers, selectedIncentiveOffers, selectedLoyaltyOffers]);
    const protectionData = useFetchLegacyProtection(paymentMethod);
    const protectionKeys = useAppSelector(selectCurrentVehicleProtectionKeys);
    const protectionsPayment = Object.fromEntries(((_c = paymentData === null || paymentData === void 0 ? void 0 : paymentData.data) === null || _c === void 0 ? void 0 : _c.protections.map(({ frequencyPayment, key }) => [key, frequencyPayment])) || []);
    const selectedProtections = (_d = protectionData === null || protectionData === void 0 ? void 0 : protectionData.data) === null || _d === void 0 ? void 0 : _d.filter(p => protectionKeys === null || protectionKeys === void 0 ? void 0 : protectionKeys.includes(p.key)).map(protection => {
        const protectionPrice = getProtectionPrice(protection.defaultAmount, protection.terms, termAndRate);
        return {
            Key: protection.key,
            Name: protection.title,
            Value: protectionPrice,
            FrequencyPayment: protectionsPayment[protection.key] || 0,
        };
    });
    const { selectedWarranty, isSelectedWarrantyRecommended, selectedWarrantyType } = useWarranties();
    const { fetchUserAuth, isLoading: isGettingUserAuth } = useFetchUserAuth();
    const { data } = useUserAuth(true);
    const vehicle = useAppSelector(selectCurrentVehicleFromVehicles);
    const { summary, response: originalResponse, ...summaryData } = useFetchCurrentVehicleFinancialCalculatorSummary(primaryCalculatorId);
    const transformedPayload = transformSaveBuildRequest(buildName, currentVehicle, selectedAccessories, selectedProtections, selectedWarranty, paymentData, summary, selectedOffers, originalResponse, isSelectedWarrantyRecommended, selectedWarrantyType, isOnlineCreditApplication);
    function generateRedirectUrl(buildKey, isCreditApplication) {
        const urlChunks = accountLoginUrl.split('?finalUrl=');
        const loginUrl = urlChunks[0] || '';
        let finalUrl = urlChunks[1] || '';
        finalUrl = finalUrl.replace('{buildKey}', buildKey);
        finalUrl = finalUrl.replace('{isOca}', String(!!isCreditApplication));
        finalUrl = encodeURIComponent(finalUrl);
        return `${loginUrl}?finalUrl=${finalUrl}`;
    }
    function updateAppState() {
        var _a, _b, _c, _d;
        dispatch(updateCurrentVehicleField({
            accessoryKeys: (_a = selectedAccessories === null || selectedAccessories === void 0 ? void 0 : selectedAccessories.map(({ Key }) => Key)) !== null && _a !== void 0 ? _a : [],
            protectionKeys: (_b = selectedProtections === null || selectedProtections === void 0 ? void 0 : selectedProtections.map(({ Key }) => Key)) !== null && _b !== void 0 ? _b : [],
        }));
        dispatch(updateCalculatorField({
            id: primaryCalculatorId,
            field: {
                loyaltyOfferKey: (_d = (_c = loyaltyOffers[0]) === null || _c === void 0 ? void 0 : _c.key) !== null && _d !== void 0 ? _d : '',
                incentiveOfferIds: filterOutNonIncentiveOffers(camelcaseKeys(selectedOffers)),
            },
        }));
        dispatch(persistCurrentVehicleToUrl());
        dispatch(persistPrimaryCalculatorToUrl());
    }
    async function saveBuild({ onSuccess, onError, }) {
        const userAuth = await fetchUserAuth();
        if (!userAuth)
            return console.error('failed to get auth data');
        const { isUserLoggedIn, accessToken } = userAuth;
        setIsLoading(true);
        const authorizationHeader = `${accessToken.tokenType} ${accessToken === null || accessToken === void 0 ? void 0 : accessToken.accessToken}`;
        const extraUrlPath = !isUserLoggedIn ? '/temporary' : '';
        const buildsApiUrl = `https://${hondaRestApiHost}/builds/${productLineLetter}${extraUrlPath}?AcceptLanguage=${language}`;
        try {
            const response = await fetch(buildsApiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: authorizationHeader,
                },
                body: JSON.stringify(transformedPayload),
            });
            if (response.ok) {
                const buildData = await response.json();
                const buildKey = buildData.BuildKey;
                onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess({
                    buildKey,
                    isUserLoggedIn,
                    redirectUrl: generateRedirectUrl(buildKey, isOnlineCreditApplication),
                });
            }
            else {
                setIsLoading(false);
                onError === null || onError === void 0 ? void 0 : onError();
                console.error('failed to send payload');
            }
        }
        catch (error) {
            setIsLoading(false);
            onError === null || onError === void 0 ? void 0 : onError();
            console.error('error sending payload');
        }
    }
    async function updateBuild({ isTemporaryBuild, onCompleted, onError, onSuccess }) {
        const userAuth = await fetchUserAuth();
        if (!userAuth)
            return console.error('failed to get auth data');
        const { accessToken, isUserLoggedIn } = userAuth;
        if (!isUserLoggedIn)
            return;
        setIsLoading(true);
        const authorizationHeader = `${accessToken.tokenType} ${accessToken === null || accessToken === void 0 ? void 0 : accessToken.accessToken}`;
        const extraUrlPath = isTemporaryBuild ? '/temporary' : '';
        const apiUrl = `https://${hondaRestApiHost}/builds/${queryBuildId}${extraUrlPath}?AcceptLanguage=${language}`;
        try {
            const response = await fetch(apiUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: authorizationHeader,
                },
                body: isTemporaryBuild
                    ? undefined
                    : JSON.stringify({ ...transformedPayload, Name: (savedBuildData === null || savedBuildData === void 0 ? void 0 : savedBuildData.name) || buildName }),
            });
            onCompleted === null || onCompleted === void 0 ? void 0 : onCompleted();
            setIsLoading(false);
            if (response.ok) {
                updateAppState();
                onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(generateRedirectUrl(queryBuildId !== null && queryBuildId !== void 0 ? queryBuildId : '', isOnlineCreditApplication));
            }
            else {
                onError === null || onError === void 0 ? void 0 : onError();
                console.error('failed to send payload');
            }
        }
        catch (error) {
            onError === null || onError === void 0 ? void 0 : onError();
            setIsLoading(false);
            console.error('error sending payload');
        }
    }
    return {
        saveBuild,
        updateBuild,
        buildId: String(queryBuildId || ''),
        isUserLoggedIn: data === null || data === void 0 ? void 0 : data.isUserLoggedIn,
        isProcessing: paymentData.isLoading || summaryData.isLoading || isGettingUserAuth || isLoading,
        hasSaveBuildData: !!(((_e = vehicle === null || vehicle === void 0 ? void 0 : vehicle.transmission) === null || _e === void 0 ? void 0 : _e.transmissionName) && (paymentData === null || paymentData === void 0 ? void 0 : paymentData.data) && originalResponse) && !(paymentData === null || paymentData === void 0 ? void 0 : paymentData.error),
    };
};
export default useSaveBuild;
export const useSaveBuildUnsavedChanges = () => {
    const [userHasUpdatedBuild, setUserHasUpdatedBuild] = useState(true);
    const { isBuildSaved, userIsBuildOwner, isUserLoggedIn, buildModelData } = useBapLoadBuild();
    const { isDiffOptions, isReady } = useBapGetComparedData();
    const canUpdateBuild = isBuildSaved && isUserLoggedIn && userIsBuildOwner;
    const hasUnsavedChanges = isReady && canUpdateBuild && isDiffOptions;
    useEffect(() => {
        // This is to make sure that the `Update build` CTA doesn't change
        // back to `Save build` even if the user undoes their changes
        if (hasUnsavedChanges) {
            setUserHasUpdatedBuild(false);
        }
    }, [hasUnsavedChanges]);
    return {
        hasUnsavedChanges,
        canUpdateBuild,
        isBuildSaved,
        userIsBuildOwner,
        isUserLoggedIn,
        isDiffOptions,
        buildModelData,
        userHasUpdatedBuild,
        onUpdateBuildSuccess() {
            setUserHasUpdatedBuild(true);
        },
    };
};
